// @mui
import Box from '@mui/material/Box';
// hooks
import { useBoolean } from '@/hooks/state/use-boolean';
import { useResponsive } from '@/hooks/ui/use-responsive';
import { useSessionHealth } from '@/hooks/auth/use-session-health';
// components
import { useSettingsContext } from '@/components/layout/settings';
//
import { useEffect } from 'react';
import { useAuthContext } from '@/auth/hooks';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { EnvIcon } from '@/components/core/env-icon';
import { ENVIRONMENT } from '../../config-global';
import Main from './main';
import Header from './header';
import NavMini from './nav-mini';
import NavVertical from './nav-vertical';
import NavHorizontal from './nav-horizontal';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export default function DashboardLayout({ children }: Props) {
  const settings = useSettingsContext();
  const { user } = useAuthContext();
  const location = useLocation();
  const lgUp = useResponsive('up', 'lg');

  // Track session health for Sentry crash free sessions
  const { trackHealthyInteraction } = useSessionHealth();

  const nav = useBoolean();

  const isHorizontal = settings.themeLayout === 'horizontal';

  const isMini = settings.themeLayout === 'mini';

  const renderNavMini = <NavMini />;

  const renderHorizontal = <NavHorizontal />;

  const renderNavVertical = <NavVertical openNav={nav.value} onCloseNav={nav.onFalse} />;

  useEffect(() => {
    // Track healthy navigation
    trackHealthyInteraction();

    // Configurar Sentry con información del usuario (en todos los ambientes)
    if (user) {
      // Set user information directly instead of using configureScope
      Sentry.setUser({
        id: user._id,
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
        position: user.position,
        isStaff: user.isStaff ? 'true' : 'false',
        isAdmin: user.isAdmin ? 'true' : 'false',
      });

      // Set tags directly
      Sentry.setTag('user._id', user._id || '');
      Sentry.setTag('user.name', `${user.firstName} ${user.lastName}`);
      Sentry.setTag('user.position', user.position || '');
      Sentry.setTag('user.email', user.email || '');
      Sentry.setTag('user.isStaff', user.isStaff ? 'true' : 'false');
      Sentry.setTag('user.isAdmin', user.isAdmin ? 'true' : 'false');

      const { screen } = window;
      Sentry.setTag('screen.resolution', `${screen.width}x${screen.height}px`);
      Sentry.setTag('screen.height', screen.height.toString());
      Sentry.setTag('screen.width', screen.width.toString());
    }

    // Configurar Intercom solo en producción
    if (['production'].includes(ENVIRONMENT) && user) {
      (window as any).intercomSettings = {
        api_base: 'https://api-iam.intercom.io',
        app_id: 'q2vbp4ii',
        name: `${user.firstName} ${user.lastName}`, // Full name
        user_id: user._id, // a UUID for your user
        Orioner: user.isStaff, // a UUID for your user
        user_hash: user.user_hash, // a UUID for your user
        email: user.email, // the email for your user
        created_at: user.createdAt, // Signup date as a Unix timestamp
        avatar: {
          type: 'avatar',
          image_url: user?.avatar?.url
            ? decodeURIComponent(user.avatar.url)
            : 'https://orion-csh.s3.amazonaws.com/media/utils/default-avatar.jpg',
        },
      };

      (function () {
        const w: any = window;
        const ic = w.Intercom;
        if (typeof ic === 'function') {
          ic('reattach_activator');
          ic('update', w.intercomSettings);
        } else {
          const d = document;
          const scriptId = 'intercom-script';
          // Remove existing Intercom script if it exists
          const existingScript = d.getElementById(scriptId);
          if (existingScript) {
            existingScript.parentNode?.removeChild(existingScript);
          }

          const i: any = function () {
            i.c(arguments);
          };
          i.q = [];

          i.c = function (args: any) {
            i.q.push(args);
          };
          w.Intercom = i;

          const l = function () {
            const s = d.createElement('script');
            s.type = 'text/javascript';
            s.async = true;
            s.id = scriptId;
            s.src = 'https://widget.intercom.io/widget/q2vbp4ii';
            const x = d.getElementsByTagName('script')[0];
            x.parentNode?.insertBefore(s, x);
          };
          if (document.readyState === 'complete') {
            l();
          } else if (w.attachEvent) {
            w.attachEvent('onload', l);
          } else {
            w.addEventListener('load', l, false);
          }
        }
      })();
    }
  }, [user, location.pathname, trackHealthyInteraction]);

  if (isHorizontal) {
    return (
      <>
        <Header onOpenNav={nav.onTrue} />

        {lgUp ? renderHorizontal : renderNavVertical}

        <Main>{children}</Main>
      </>
    );
  }

  if (isMini) {
    return (
      <>
        <Header onOpenNav={nav.onTrue} />

        <Box
          sx={{
            minHeight: 1,
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
          }}
        >
          {lgUp ? renderNavMini : renderNavVertical}

          <Main>{children}</Main>
        </Box>
      </>
    );
  }

  return (
    <>
      <Header onOpenNav={nav.onTrue} />

      <Box
        sx={{
          minHeight: 1,
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
        }}
      >
        <EnvIcon />

        {renderNavVertical}

        <Main>{children}</Main>
      </Box>
    </>
  );
}
